import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import CountUp from "@/components/CountUp";
import ConvergenceMotif from "@/components/ConvergenceMotif";
import { useI18n } from "@/lib/i18n";
import { formatApiError } from "@/lib/auth";
import { ArrowUpRight, ArrowRight, Menu, X, Instagram, Linkedin } from "lucide-react";
import LogoMarquee from "@/components/LogoMarquee";
import ConsultExpertDialog from "@/components/ConsultExpertDialog";
import FileCaseAuthDialog from "@/components/FileCaseAuthDialog";

// ─────────────────── shared bits ───────────────────
const Eyebrow = ({ children, light }) => (
  <div className={light ? "eyebrow-light" : "eyebrow"}>{children}</div>
);

const BtnPrimary = ({ children, to, href, onClick, testid, className = "" }) => {
  const Cmp = to ? Link : (href ? "a" : "button");
  const props = to ? { to } : href ? { href } : { onClick, type: "button" };
  return (
    <Cmp
      data-testid={testid}
      {...props}
      className={`group inline-flex items-center gap-2 px-5 py-3 text-sm font-medium tracking-tight bg-clay text-sand hover:bg-[hsl(17_64%_42%)] transition-colors ${className}`}
      style={{ borderRadius: 3 }}
    >
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </Cmp>
  );
};

const BtnGhost = ({ children, to, onClick, testid, tone = "ink", className = "" }) => {
  const Cmp = to ? Link : "button";
  const props = to ? { to } : { onClick, type: "button" };
  const color = tone === "sand" ? "text-sand border-sand/40 hover:border-sand" : "text-ink border-ink/30 hover:border-ink";
  return (
    <Cmp
      data-testid={testid}
      {...props}
      className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium tracking-tight border bg-transparent transition-colors ${color} ${className}`}
      style={{ borderRadius: 3 }}
    >
      {children} <ArrowRight className="h-4 w-4" />
    </Cmp>
  );
};

// ─────────────────── nav ───────────────────
function Nav() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-sand border-b hairline">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-home" className="shrink-0"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-8 text-[13px] text-ink/80">
          <a href="#institutions" data-testid="nav-businesses" className="hover:text-ink transition">{t("nav.businesses")}</a>
          <a href="#individuals" data-testid="nav-individuals" className="hover:text-ink transition">{t("nav.individuals")}</a>
          <a href="#resolutionary" data-testid="nav-resolutionary" className="hover:text-ink transition">{t("nav.resolutionary")}</a>
          <Link to="/faqs" data-testid="nav-faqs" className="hover:text-ink transition">{t("nav.faqs")}</Link>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="hidden sm:inline-flex font-mono text-[11px] tracking-[0.12em] uppercase text-ink/60 hover:text-ink px-2 py-1"
          >
            {lang === "en" ? "EN · हिं" : "हिं · EN"}
          </button>
          <Link to="/login" data-testid="nav-login" className="hidden sm:inline-block text-[13px] text-ink/80 hover:text-ink px-2">{t("nav.login")}</Link>
          <Link
            to="/register" data-testid="nav-register"
            className="group inline-flex items-center gap-1.5 px-3 sm:px-5 py-2.5 sm:py-3 text-[12px] sm:text-sm font-medium tracking-tight bg-clay text-sand hover:bg-[hsl(17_64%_42%)] transition-colors"
            style={{ borderRadius: 3 }}
          >
            <span>Register</span>
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            className="lg:hidden text-ink/70 hover:text-ink p-1 -mr-1"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="nav-mobile-drawer">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 max-w-[85vw] bg-sand flex flex-col">
            <div className="h-16 border-b hairline flex items-center justify-between px-5">
              <Logo />
              <button onClick={() => setOpen(false)} className="text-ink/70 hover:text-ink p-1" aria-label="Close menu" data-testid="nav-mobile-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="p-5 flex-1 space-y-1">
              {[
                ["nav.businesses", "#institutions"],
                ["nav.individuals", "#individuals"],
                ["nav.resolutionary", "#resolutionary"],
              ].map(([k, href]) => (
                <a key={k} href={href} onClick={() => setOpen(false)} className="block font-display text-[24px] text-ink py-2 hover:text-clay">{t(k)}</a>
              ))}
              <Link to="/faqs" onClick={() => setOpen(false)} className="block font-display text-[24px] text-ink py-2 hover:text-clay">{t("nav.faqs")}</Link>
              <Link to="/panel" onClick={() => setOpen(false)} className="block font-display text-[24px] text-ink py-2 hover:text-clay">Our mediators & arbitrators</Link>
            </nav>
            <div className="p-5 border-t hairline space-y-3">
              <Link to="/login" onClick={() => setOpen(false)} className="block text-[14px] text-ink hover:text-clay" data-testid="nav-mobile-login">{t("nav.login")} →</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="block text-[14px] text-ink hover:text-clay" data-testid="nav-mobile-register">Register →</Link>
              <button
                onClick={() => setLang(lang === "en" ? "hi" : "en")}
                className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink/60"
                data-testid="nav-mobile-lang"
              >
                {lang === "en" ? "Switch to हिंदी" : "Switch to English"}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// ─────────────────── hero ───────────────────
function Hero() {
  const { openConsult, openFileCase } = React.useContext(LandingDialogsCtx);
  return (
    <section className="bg-court text-sand relative overflow-hidden" data-testid="hero">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <Eyebrow light>Online Dispute Resolution · India</Eyebrow>
          <h1 className="font-display mt-6 text-[38px] sm:text-[52px] lg:text-[68px] balanced" style={{ fontWeight: 400, lineHeight: 1.0 }}>
            <span className="block anim-converge-left">Resolve disputes.</span>
            <span className="block text-sand/75 anim-converge-right" style={{ fontWeight: 300 }}>Restore relationships.</span>
            <span className="block anim-meet" style={{ height: 2, background: "#C2542A", marginTop: 22 }} />
          </h1>
          <p className="mt-8 max-w-xl text-sand/80 text-[17px] pretty">
            Welcome to eSulah — we are reimagining access to justice for modern India, bringing mediation, arbitration and conciliation directly to your screen. No court visits. No endless waiting. Just fair, legally binding resolutions.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <BtnPrimary onClick={openFileCase} testid="hero-file-case">File a case</BtnPrimary>
            <BtnGhost onClick={openConsult} tone="sand" testid="hero-consult-expert">Consult an expert</BtnGhost>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md font-mono text-[11px] tracking-[0.12em] uppercase text-sand/55">
            <div><div className="text-sand/85 text-[13px] font-sans mb-1">Mediation</div>Amicable</div>
            <div><div className="text-sand/85 text-[13px] font-sans mb-1">Arbitration</div>Binding</div>
            <div><div className="text-sand/85 text-[13px] font-sans mb-1">Conciliation</div>Guided</div>
          </div>
        </div>
        <div className="lg:col-span-5">
          <ConvergenceMotif tone="court" />
        </div>
      </div>
    </section>
  );
}

// ─────────────────── trust bar ───────────────────
function TrustBar() {
  return (
    <section className="bg-sand border-y hairline">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-8 text-center">
        <div className="font-mono text-[11px] sm:text-[12px] tracking-[0.22em] uppercase text-ink/65 balanced">
          Trusted by modern Indian enterprises, MSMEs, and everyday citizens for justice.
        </div>
      </div>
    </section>
  );
}

// ─────────────────── mission (How eSulah Works) ───────────────────
function Mission() {
  return (
    <section id="what" className="bg-sand">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2"><Eyebrow>How eSulah works</Eyebrow></div>
        <div className="lg:col-span-7">
          <h2 className="font-display text-[28px] sm:text-[36px] lg:text-[48px] text-court balanced" style={{ fontWeight: 400, lineHeight: 1.06 }}>
            Expert mediation with eSulah.
          </h2>
          <p className="mt-6 text-ink/80 text-[16px] lg:text-[17px] max-w-2xl pretty">
            Forget the endless court dates. Whether you are dealing with a delayed payment or a contract issue, eSulah connects you with certified mediators and arbitrators to reach a friendly, legally enforceable settlement — from the comfort of your home.
          </p>
        </div>
        <div className="lg:col-span-3 self-end">
          <p className="text-ink/70 text-[14px] pretty">
            eSulah is an advanced legal-tech platform that moves the entire dispute resolution process online — under the Indian Arbitration &amp; Conciliation Act, 1996.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─────────────────── tailored resolution ───────────────────
function Tailored() {
  const items = [
    { idx: "01", tag: "For businesses",         title: "Save time and legal costs.",    body: "Ideal for e-commerce, banking, quick commerce and FMCG supply chains. eSulah reduces legal costs by up to 80% and preserves your critical business relationships.", cta: "Learn more",         href: "#institutions" },
    { idx: "02", tag: "For individuals",        title: "Skip the endless court dates.", body: "Stuck in a dispute? Get a 60-minute online consultation with a subject-matter expert, and file your case for affordable online mediation with certified Indian mediators — from your smartphone.", cta: "Resolve my dispute", href: "#individuals" },
    { idx: "03", tag: "For legal professionals", title: "Shape the future of justice.",  body: "Join our panel of certified Arbitrators, Mediators and Case Managers to handle disputes digitally — and grow your practice.", cta: "Join the panel", href: "#resolutionary" },
  ];
  return (
    <section id="institutions" className="bg-sand border-t hairline">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-2"><Eyebrow>Tailored resolution</Eyebrow></div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[48px] text-court balanced" style={{ fontWeight: 400, lineHeight: 1.04 }}>
              One platform. Three audiences. Every kind of dispute.
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-stone/60 border hairline">
          {items.map((it) => (
            <a key={it.idx} href={it.href} data-testid={`tailored-${it.idx}`} className="bg-sand p-8 lift-on-hover group flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-clay">{it.idx}</div>
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-stone">{it.tag}</div>
              </div>
              <h3 className="font-display text-[24px] lg:text-[28px] text-ink" style={{ fontWeight: 400, lineHeight: 1.1 }}>{it.title}</h3>
              <p className="mt-4 text-ink/70 text-[14px] flex-1">{it.body}</p>
              <div className="mt-6 font-mono text-[11px] tracking-[0.14em] uppercase text-ink group-hover:text-clay transition-colors flex items-center gap-1.5">
                {it.cta} <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────── stats ───────────────────
function Stats({ data }) {
  const { t } = useI18n();
  const items = [
    { label: t("stats.filed"), end: data?.disputes_filed || 10000, suffix: "+" },
    { label: t("stats.resolved"), end: data?.disputes_resolved || 7500, suffix: "+" },
    { label: t("stats.avg"), end: data?.avg_days || 38, suffix: ` ${t("stats.days")}` },
    { label: t("stats.panel"), end: data?.neutrals_count || 1000, suffix: "+" },
  ];
  return (
    <section className="bg-ink text-sand">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 lg:grid-cols-4 divide-y divide-sand/15 lg:divide-y-0 lg:divide-x">
        {items.map((it) => (
          <div key={it.label} className="px-0 lg:px-8 py-6" data-testid={`stat-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <Eyebrow light>{it.label}</Eyebrow>
            <div className="font-display mt-3 text-[44px] lg:text-[56px]" style={{ fontWeight: 400, lineHeight: 1 }}>
              <CountUp end={it.end} />{it.suffix.includes("days") ? <span className="text-[20px] text-sand/60 ml-2 font-mono">{t("stats.days")}</span> : <span className="text-clay">{it.suffix.replace(/ days/, "")}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// (Institutions replaced by Tailored above)

// ─────────────────── the eSulah advantage ───────────────────
function Features() {
  const items = [
    { idx: "01", title: "Legally enforceable", body: "Every eSulah arbitral award is a legally binding decree, enforceable in Indian courts under the Arbitration & Conciliation Act, 1996." },
    { idx: "02", title: "Smart document review", body: "Upload your evidence — invoices, WhatsApp chats, contracts. Our platform classifies documents and helps mediators & arbitrators begin with structure, not chaos." },
    { idx: "03", title: "Secure & private", body: "Hosted on highly secure, encrypted Indian servers ensuring 100% data localization and ISO 27001-aligned controls. Every case, strictly confidential." },
    { idx: "04", title: "Multilingual support", body: "Justice speaks your language. The platform and our mediators & arbitrators support dispute resolution in multiple Indian regional languages." },
  ];
  return (
    <section className="bg-court text-sand">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-2"><Eyebrow light>The eSulah advantage</Eyebrow></div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-[32px] lg:text-[48px] balanced" style={{ fontWeight: 400, lineHeight: 1.05 }}>
              Why choose eSulah.
            </h2>
          </div>
        </div>
        <ul className="grid sm:grid-cols-2 divide-x divide-sand/15">
          {items.map((it, i) => (
            <li key={it.idx} className={`p-8 lg:p-10 ${i < 2 ? "border-b border-sand/15" : ""}`} data-testid={`advantage-${it.idx}`}>
              <div className="font-mono text-[11px] tracking-[0.16em] text-clay">{it.idx}</div>
              <h3 className="font-display mt-4 text-[24px]" style={{ fontWeight: 400 }}>{it.title}</h3>
              <p className="mt-3 text-sand/75 text-[14px] max-w-sm">{it.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ─────────────────── enforceability ───────────────────
function Enforceability() {
  const { t } = useI18n();
  return (
    <section className="bg-sand border-t hairline">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-2"><Eyebrow>{t("section.enforce")}</Eyebrow></div>
        <div className="lg:col-span-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-display text-[32px] lg:text-[44px] text-court balanced" style={{ fontWeight: 400, lineHeight: 1.05 }}>
              A settlement here holds in any court in India.
            </h2>
            <p className="mt-6 text-ink/75 text-[15px] max-w-xl pretty">
              Outcomes issued on eSulah are binding under the Mediation Act, 2023 and the Arbitration &amp; Conciliation Act, 1996. India-hosted infrastructure, encrypted in transit and at rest, ISO 27001-aligned controls, panel-based appointment of mediators & arbitrators.
            </p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-stone/60 border hairline">
            {[
              { k: "Binding", v: "awards & settlements" },
              { k: "Encrypted", v: "in transit & at rest" },
              { k: "India-hosted", v: "infrastructure" },
              { k: "Empaneled", v: "qualified mediators & arbitrators" },
            ].map((x) => (
              <div key={x.k} className="bg-sand p-5">
                <div className="font-display text-[20px] text-court" style={{ fontWeight: 400 }}>{x.k}</div>
                <div className="mt-1 text-ink/65 text-[13px]">{x.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────── testimonials ───────────────────
const TESTIMONIALS = [
  { quote: "We moved 240 small-ticket recovery disputes to eSulah. Average cycle dropped from nine months to thirty-two days. The customers, remarkably, thanked us.", who: "Head of Operations · Fintech NBFC", date: "Feb 2026" },
  { quote: "The dashboard finally gave us one pane of glass across legal, ops and customer experience. The audit trail alone has been worth it.", who: "General Counsel · E-commerce Platform", date: "Jan 2026" },
  { quote: "The workroom is clean. Evidence is classified before I open it. Transcripts mean I look at the parties, not at my notepad.", who: "Empaneled Mediator · Mumbai", date: "Dec 2025" },
];

function Testimonials() {
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI(x => (x + 1) % TESTIMONIALS.length), 7000); return () => clearInterval(id); }, []);
  const t = TESTIMONIALS[i];
  return (
    <section className="bg-ink text-sand">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2"><Eyebrow light>In their words</Eyebrow></div>
        <div className="lg:col-span-10" data-testid="testimonial">
          <p className="font-display text-[28px] lg:text-[40px] balanced italic" style={{ fontWeight: 300, lineHeight: 1.15 }}>
            &ldquo;{t.quote}&rdquo;
          </p>
          <div className="mt-10 flex items-center justify-between border-t hairline-light pt-5">
            <div className="font-mono text-[12px] tracking-[0.12em] text-sand/70 uppercase">{t.who}</div>
            <div className="font-mono text-[12px] tracking-[0.12em] text-sand/40">{t.date}</div>
          </div>
          <div className="flex gap-1 mt-6">
            {TESTIMONIALS.map((_, k) => (
              <button key={k} aria-label={`Testimonial ${k+1}`} data-testid={`testimonial-dot-${k}`} onClick={() => setI(k)} className={`h-px transition-all ${k === i ? "w-10 bg-clay" : "w-6 bg-sand/30"}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────── recognised across ───────────────────
function Recognised() {
  const { t } = useI18n();
  const Seal = LogoMarquee.SealMarks;
  const ink = "#15201C";

  const platforms = [
    { name: "Razorpay", caption: "Payments" },
    { name: "PhonePe", caption: "Payments" },
    { name: "Cred", caption: "Cards" },
    { name: "Zerodha", caption: "Broking" },
    { name: "Flipkart", caption: "E-commerce" },
    { name: "Meesho", caption: "Marketplace" },
    { name: "PolicyBazaar", caption: "Insurance" },
    { name: "Paytm", caption: "Payments" },
    { name: "Groww", caption: "Investments" },
    { name: "Swiggy", caption: "Logistics" },
    { name: "Zomato", caption: "F&B" },
    { name: "Upstox", caption: "Broking" },
  ];
  const courts = [
    { name: "Delhi High Court", caption: "High Court · Delhi", icon: Seal.pillars(ink) },
    { name: "Bombay High Court", caption: "High Court · Maharashtra", icon: Seal.pillars(ink) },
    { name: "Karnataka High Court", caption: "High Court · Karnataka", icon: Seal.pillars(ink) },
    { name: "Madras High Court", caption: "High Court · Tamil Nadu", icon: Seal.pillars(ink) },
    { name: "Calcutta High Court", caption: "High Court · West Bengal", icon: Seal.pillars(ink) },
    { name: "Allahabad High Court", caption: "High Court · UP", icon: Seal.pillars(ink) },
    { name: "Lok Adalat", caption: "Statutory body", icon: Seal.scales(ink) },
  ];
  const gov = [
    { name: "Ministry of Justice", caption: "Department of Justice", icon: Seal.ashoka(ink) },
    { name: "NITI Aayog", caption: "Govt. of India", icon: Seal.ashoka(ink) },
    { name: "Ministry of MSME", caption: "Govt. of India", icon: Seal.shield(ink) },
    { name: "Consumer Affairs", caption: "Department", icon: Seal.shield(ink) },
    { name: "RBI", caption: "Reserve Bank of India", icon: Seal.scales(ink) },
    { name: "SEBI", caption: "Securities & Exchange Board", icon: Seal.shield(ink) },
  ];

  return (
    <section className="bg-sand border-t hairline">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2"><Eyebrow>{t("section.trusted")}</Eyebrow></div>
        <div className="lg:col-span-10 min-w-0">
          <Tabs defaultValue="platforms" className="w-full">
            <TabsList className="bg-transparent border-b hairline rounded-none p-0 gap-8 h-auto mb-8">
              <TabsTrigger value="platforms" data-testid="trusted-tab-platforms" className="font-mono text-[11px] tracking-[0.16em] uppercase data-[state=active]:text-clay data-[state=active]:border-b-2 data-[state=active]:border-clay rounded-none bg-transparent px-0 pb-3">Platforms</TabsTrigger>
              <TabsTrigger value="courts" data-testid="trusted-tab-courts" className="font-mono text-[11px] tracking-[0.16em] uppercase data-[state=active]:text-clay data-[state=active]:border-b-2 data-[state=active]:border-clay rounded-none bg-transparent px-0 pb-3">Courts</TabsTrigger>
              <TabsTrigger value="gov" data-testid="trusted-tab-government" className="font-mono text-[11px] tracking-[0.16em] uppercase data-[state=active]:text-clay data-[state=active]:border-b-2 data-[state=active]:border-clay rounded-none bg-transparent px-0 pb-3">Government</TabsTrigger>
            </TabsList>
            <TabsContent value="platforms" className="mt-0">
              <LogoMarquee items={platforms} tone="sand" speed={48} testid="marquee-platforms" />
              <p className="mt-4 font-mono text-[11px] tracking-[0.12em] uppercase text-stone">12 integrations · live · monochrome marks for institutional consistency</p>
            </TabsContent>
            <TabsContent value="courts" className="mt-0">
              <LogoMarquee items={courts} tone="sand" speed={55} testid="marquee-courts" />
              <p className="mt-4 font-mono text-[11px] tracking-[0.12em] uppercase text-stone">High courts &amp; statutory bodies recognising ODR outcomes</p>
            </TabsContent>
            <TabsContent value="gov" className="mt-0">
              <LogoMarquee items={gov} tone="sand" speed={55} testid="marquee-government" />
              <p className="mt-4 font-mono text-[11px] tracking-[0.12em] uppercase text-stone">Aligned with Indian ministries &amp; regulators</p>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}

// ─────────────────── individuals ───────────────────
function Individuals() {
  const { t } = useI18n();
  const { openConsult } = React.useContext(LandingDialogsCtx);
  return (
    <section id="individuals" className="bg-court text-sand">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2"><Eyebrow light>{t("section.individuals")}</Eyebrow></div>
        <div className="lg:col-span-7">
          <h2 className="font-display text-[32px] lg:text-[48px] balanced" style={{ fontWeight: 400, lineHeight: 1.05 }}>
            Talk to a person who has seen this before.
          </h2>
          <p className="mt-6 text-sand/80 text-[16px] max-w-lg pretty">
            A sixty-minute consultation with a qualified expert. If your matter is suitable for online resolution, we'll file it for online mediation or arbitration. If it isn't, we'll tell you straight.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <BtnPrimary onClick={openConsult} testid="individuals-consult-expert">Consult an expert</BtnPrimary>
            <Link to="/faqs" className="text-sand/70 hover:text-sand text-[13px] inline-flex items-center gap-1 self-center" data-testid="individuals-faqs-link">Read the FAQs <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>
        <div className="lg:col-span-3 border-l hairline-light pl-6 self-end">
          <div className="eyebrow-light">What you get</div>
          <ul className="mt-4 space-y-3 text-sand/80 text-[14px]">
            <li>· Suitability check for ODR</li>
            <li>· Document &amp; strategy review</li>
            <li>· Filing assistance if eligible</li>
            <li>· Honest opinion if not</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─────────────────── resolutionary ───────────────────
function Resolutionary() {
  const { t } = useI18n();
  return (
    <section id="resolutionary" className="bg-sand border-t hairline">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2"><Eyebrow>{t("section.resolutionary")}</Eyebrow></div>
        <div className="lg:col-span-7">
          <h2 className="font-display text-[32px] lg:text-[48px] text-court balanced" style={{ fontWeight: 400, lineHeight: 1.05 }}>
            A modern path into dispute resolution.
          </h2>
          <p className="mt-6 text-ink/75 text-[15px] max-w-lg pretty">
            Two steps. Complete eSulah's online ODR course, then choose your path — case management, mediation, or arbitration. Empanelment is by application; the work is online.
          </p>
          <div className="mt-8"><BtnPrimary to="/register" testid="resolutionary-signup">Sign up for the course</BtnPrimary></div>
        </div>
        <div className="lg:col-span-3 border-l hairline pl-6">
          <ol className="space-y-6">
            {[
              { k: "Step 01", t: "ODR course", b: "Fifteen hours, self-paced. Indian ODR law, mediation craft, platform tooling." },
              { k: "Step 02", t: "Empanelment", b: "Apply, get reviewed, get assigned cases. The work begins the same week." },
            ].map((s) => (
              <li key={s.k}>
                <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-clay">{s.k}</div>
                <div className="mt-1 font-display text-[20px] text-ink" style={{ fontWeight: 400 }}>{s.t}</div>
                <div className="text-ink/65 text-[13px] mt-1">{s.b}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

// ─────────────────── contact ───────────────────
function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", type: "", message: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.type || !form.message) {
      toast.error("Please fill the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact-leads", form);
      setSent(true);
      toast.success("Message received. We'll be in touch within one business day.");
      setForm({ name: "", organization: "", email: "", phone: "", type: "", message: "", city: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const fieldCls = "h-12 rounded-none bg-transparent border-0 border-b border-sand/30 focus:border-clay text-sand placeholder:text-sand/45 px-0";

  return (
    <section id="contact" className="bg-ink text-sand">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-2"><Eyebrow light>{t("section.contact")}</Eyebrow></div>
        <div className="lg:col-span-10">
          <h2 className="font-display text-[32px] lg:text-[48px] balanced max-w-2xl" style={{ fontWeight: 400, lineHeight: 1.05 }}>
            Let's talk resolution.
          </h2>
          <p className="mt-4 text-sand/75 text-[15px] max-w-xl">
            Have a question about a case, our platform, or enterprise partnerships? Drop us a line — or write to <a href="mailto:support@e-sulah.com" className="text-clay underline-offset-4 hover:underline">support@e-sulah.com</a>. We'll reply within one business day.
          </p>
          {sent ? (
            <div className="mt-12 border hairline-light p-10" data-testid="contact-success">
              <div className="eyebrow-light">Received</div>
              <div className="font-display text-[24px] mt-3">Thank you. We'll be in touch.</div>
              <button onClick={() => setSent(false)} className="mt-6 text-sand/70 hover:text-sand text-[13px] underline">Send another message →</button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-6" data-testid="contact-form">
              <Input data-testid="contact-name" placeholder="Name *" value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls} />
              <Input data-testid="contact-organization" placeholder="Organisation" value={form.organization} onChange={(e) => set("organization", e.target.value)} className={fieldCls} />
              <Input data-testid="contact-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} />
              <Input data-testid="contact-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={fieldCls} />
              <div>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="contact-type-trigger" className={fieldCls}><SelectValue placeholder="Type of query *" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business">Business enquiry</SelectItem>
                    <SelectItem value="government">Government enquiry</SelectItem>
                    <SelectItem value="neutral">Join as a mediator/arbitrator</SelectItem>
                    <SelectItem value="careers">Careers</SelectItem>
                    <SelectItem value="file">File a dispute</SelectItem>
                    <SelectItem value="notice">I received a notice</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input data-testid="contact-city" placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className={fieldCls} />
              <Textarea data-testid="contact-message" placeholder="Message *" value={form.message} onChange={(e) => set("message", e.target.value)} className={`md:col-span-2 min-h-[120px] rounded-none bg-transparent border-0 border-b border-sand/30 focus:border-clay text-sand placeholder:text-sand/45 px-0 py-2`} />
              <div className="md:col-span-2 flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-2 px-5 py-3 text-sm font-medium tracking-tight bg-clay text-sand hover:bg-[hsl(17_64%_42%)] transition-colors disabled:opacity-60"
                  style={{ borderRadius: 3 }}
                >
                  {loading ? "Sending…" : "Send message"}
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

// ─────────────────── footer ───────────────────
function Footer() {
  const { t } = useI18n();
  return (
    <footer className="bg-ink text-sand/85">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-10 border-t hairline-light">
        <div className="grid lg:grid-cols-12 gap-y-12 lg:gap-10">
          <div className="lg:col-span-5">
            <Logo tone="sand" />
            <div className="mt-6 max-w-md text-[13px] leading-relaxed pretty" style={{ color: "hsl(var(--sand) / 0.92)" }}>
              <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-clay mb-2">Disclaimer</div>
              <p>
                Please note that eSulah is <span className="font-medium" style={{ color: "hsl(var(--sand))" }}>not a law firm</span> and does not engage in the practice of law or solicit lawyers.
              </p>
              <p className="mt-3">
                eSulah is an advanced, technology platform designed to facilitate Online Dispute Resolution (ODR) services. We aim to provide an accessible and convenient way for users to seek guidance and support from independent professionals in resolving their disputes. All interactions or consultations facilitated through eSulah are between users and independent professionals. eSulah is not responsible for any advice or services provided by these independent professionals.
              </p>
            </div>
          </div>

          {/* Link groups: side-by-side 3 columns on mobile too */}
          <div className="lg:col-span-7 grid grid-cols-3 gap-x-4 sm:gap-x-8 gap-y-8">
            {[
              { h: "Product", links: [["Sign in","/login"], ["File a dispute","/register"], ["Mediators & arbitrators","/panel"]] },
              { h: "Company", links: [["About","#what"], ["Resolutionary","#resolutionary"], ["FAQs","/faqs"]] },
              { h: "Legal", links: [["Privacy Policy","/privacy"], ["Terms of Service","/terms"], ["User Agreement","/user-agreement"]] },
            ].map((g) => (
              <div key={g.h}>
                <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-clay">{g.h}</div>
                <ul className="mt-4 space-y-2.5 text-[14px] sm:text-[15px]">
                  {g.links.map(([txt, to]) => (
                    <li key={txt}>
                      {to.startsWith("/")
                        ? <Link to={to} className="text-sand hover:text-clay transition-colors">{txt}</Link>
                        : <a href={to} className="text-sand hover:text-clay transition-colors">{txt}</a>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t hairline-light mt-10 lg:mt-14 pt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-[11px] font-mono tracking-[0.12em] uppercase" style={{ color: "hsl(var(--sand) / 0.55)" }}>
          <div>© {new Date().getFullYear()} eSulah. All rights reserved.</div>
          <div className="flex items-center gap-5 sm:gap-6">
            <span className="hidden sm:inline">Samadhan, Sammaan ke Saath</span>
            <div className="flex items-center gap-4" data-testid="footer-socials">
              <a
                href="https://www.instagram.com/esulah.in/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="eSulah on Instagram"
                data-testid="footer-instagram"
                className="transition-colors hover:!text-clay"
                style={{ color: "hsl(var(--sand))" }}
              >
                <Instagram className="h-5 w-5" strokeWidth={1.75} />
              </a>
              <a
                href="https://www.linkedin.com/company/esulah/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="eSulah on LinkedIn"
                data-testid="footer-linkedin"
                className="transition-colors hover:!text-clay"
                style={{ color: "hsl(var(--sand))" }}
              >
                <Linkedin className="h-5 w-5" strokeWidth={1.75} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────── page ───────────────────
const LandingDialogsCtx = React.createContext({ openConsult: () => {}, openFileCase: () => {} });

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);
  const [fileCaseOpen, setFileCaseOpen] = useState(false);
  useEffect(() => { api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {}); }, []);
  return (
    <LandingDialogsCtx.Provider value={{
      openConsult: () => setConsultOpen(true),
      openFileCase: () => setFileCaseOpen(true),
    }}>
      <div className="min-h-screen bg-sand overflow-x-hidden" data-testid="landing-page">
        <Nav />
        <main>
          <Hero />
          <TrustBar />
          <Mission />
          <Stats data={stats} />
          <Tailored />
          <Features />
          <Enforceability />
          <Testimonials />
          <Recognised />
          <Individuals />
          <Resolutionary />
          <Contact />
        </main>
        <Footer />
        <ConsultExpertDialog open={consultOpen} onOpenChange={setConsultOpen} />
        <FileCaseAuthDialog open={fileCaseOpen} onOpenChange={setFileCaseOpen} />
      </div>
    </LandingDialogsCtx.Provider>
  );
}
